import React, { useMemo, useState, useEffect } from 'react'
import { PageHeader, CommonFilters, TableDownload, UserAvatar } from '@components'
import { Card, Table, TableProps, Button, Popover, Descriptions, message } from 'antd'
import { formatTime, trimStr } from '@library'
import { useTableService } from '@hooks/useTableService'
import { getLeadIntentionAndPipelineList, exportLeadIntentionAndPipelineList } from '@services/lead'
import { needTotalEnum, LeadIntentionAndPipelineItem, LeadIntentionAndPipelineListFilter, IFollowFormItem, FormItemType } from '@types'
import { ConfigTableHeader } from 'src/components/ConfigTableHeader'
import { TableIdEnum, getTableXwidth, getSortOrder } from '@configs/table'
import { useTableColumn } from '@hooks'
import { antdSortMap } from '@configs/map'
import { Link } from 'react-router-dom'
import { clone } from 'lodash'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { getFollowFormItemList } from '@services/customer'

export default function LeadListRecallPage () {
  // 初始筛选条件
  const initFilter = useMemo<LeadIntentionAndPipelineListFilter>(() => {
    return {
      page: 1,
      pageSize: 10,
      needTotal: needTotalEnum.ALL,
      status: 7,
    }
  }, [])

  // 表格服务
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
  } = useTableService(getLeadIntentionAndPipelineList, initFilter, '获取意向线索列表失败')
  const [followFormItemList, setFollowFormItemList] = useState<IFollowFormItem[]>([])
  useEffect(() => {
    const queryFollowFormItemList = async () => {
      const [res, err] = await getFollowFormItemList()
      if (err) {
        message.error(`获取渲染表单数据失败，请刷新页面重试(${err.message})`)
      } else {
        setFollowFormItemList(res?.list ?? [])
      }
    }
    queryFollowFormItemList()
  }, [])
  const recallLeadList = useMemo<any[]>(() => list?.map(item => {
    const recallLeadItem: any = clone(item)
    const followRecordExtend = JSON.parse(recallLeadItem?.followRecord?.extend) || []
    followRecordExtend.forEach(item => {
      const formItem = followFormItemList.find(formItem => formItem.key === item.key)
      if (formItem) {
        if (formItem.type === FormItemType.SELECT || formItem.type === FormItemType.MUTIL_SELECT) {
          item.keyValue = formItem.options.find(option => option.optionValue === item.keyValue)?.optionName
        }
      }
    })
    recallLeadItem.interestProduct = recallLeadItem.interestSmallProductName && `${recallLeadItem.interestSmallProductName}-${recallLeadItem.interestSubProductName}`
    recallLeadItem.customerIntention = `${recallLeadItem.intentionName}-${recallLeadItem.subIntentionName}`
    recallLeadItem.details = [
      { keyName: '本次联系人', keyValue: recallLeadItem?.followRecord?.touchCustomerName },
      { keyName: '本次沟通方式', keyValue: recallLeadItem?.followRecord?.followType },
      { keyName: '沟通是否有效', keyValue: recallLeadItem?.followRecord?.communicateResult },
      { keyName: '客户最新意向', keyValue: `${recallLeadItem?.followRecord?.intentionName}-${recallLeadItem?.followRecord?.subIntentionName}` },
      { keyName: '本次跟进内容', keyValue: recallLeadItem?.followRecord?.remark },
      { keyName: '感兴趣的产品', keyValue: recallLeadItem?.followRecord?.interestSmallProductName && `${recallLeadItem?.followRecord?.interestSmallProductName}-${recallLeadItem?.followRecord?.interestSubProductName}` },
      ...followRecordExtend,
    ]
    return recallLeadItem
  }), [list])
  // 筛选项配置
  const recallFilterItems: FieldItem[] = [
    { fieldName: 'customerName', label: '客户名称', type: 'customerNameSelect' },
    // { fieldName: 'taskId', label: '任务ID', type: 'input' },
    // { fieldName: 'taskName', label: '任务名称', type: 'input' },
    // {
    //   fieldName: 'taskCreateTime',
    //   label: '任务创建时间',
    //   type: 'rangePicker',
    //   handleValue: val => ({
    //     taskCreateTimeFrom: formatTime(val?.[0], 'start'),
    //     taskCreateTimeTo: formatTime(val?.[1], 'end'),
    //   }),
    // },
    {
      fieldName: 'createTime',
      label: '线索创建时间',
      type: 'rangePicker',
      handleValue: val => ({
        createTimeFrom: formatTime(val?.[0], 'start'),
        createTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    { fieldName: 'batchId', label: '批次名称', type: 'batchId' },
    {
      fieldName: 'lastFollowTime',
      label: '跟进时间',
      type: 'rangePicker',
      handleValue: val => ({
        lastFollowTimeBegin: formatTime(val?.[0], 'start'),
        lastFollowTimeEnd: formatTime(val?.[1], 'end'),
      }),
    },
    {
      fieldName: 'follower',
      label: '跟进人',
      type: 'staffSelectMulti',
    },
  ]

  // 表格列定义
  const columns = [
    {
      title: '线索ID',
      dataIndex: 'leadId',
      width: 120,
    },
    {
      title: '线索名称',
      dataIndex: 'leadName',
      width: 150,
    },
    {
      title: '客户名称',
      dataIndex: 'name',
      width: 150,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>
      ),
    },
    {
      title: '客户意向',
      dataIndex: 'customerIntention',
      width: 120,
    },
    {
      title: '感兴趣的产品',
      dataIndex: 'interestProduct',
      width: 150,
    },
    {
      title: '本次跟进内容',
      dataIndex: 'followContent',
      width: 200,
      ellipsis: true,
    },
    {
      title: '联系人名称',
      dataIndex: 'contactName',
      width: 120,
    },
    {
      title: '职务',
      dataIndex: 'contactPosition',
      width: 120,
    },
    {
      title: '跟进人',
      dataIndex: 'follower',
      width: 220,
      render: (text) => <UserAvatar salesName={text} />,
    },
    {
      title: '跟进时间',
      dataIndex: 'followTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'followTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '线索创建时间',
      dataIndex: 'createTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '任务ID',
      dataIndex: 'taskId',
      width: 120,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      width: 150,
    },
    {
      title: '任务创建时间',
      dataIndex: 'taskCreateTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'taskCreateTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '批次ID',
      dataIndex: 'batchId',
      width: 120,
    },
    {
      title: '批次名称',
      dataIndex: 'batchId',
      width: 150,
    },
    {
      title: '详情',
      dataIndex: 'details',
      width: 80,
      render: (text) => (
        <Popover content={
          <Descriptions column={2} size="small" style={{ width: '600px' }}>
            {text?.map?.((item, index) => {
              return <Descriptions.Item key={`${item.keyName}-${index}`} label={item.keyName}>{item.keyValue === 'undefined' ? '-' : item.keyValue}</Descriptions.Item>
            })}
          </Descriptions>
        } title="详情">
          <Button type="link" size="small">详情</Button>
        </Popover>
      ),
    },
  ]

  const [columnConfigs, columnProps, forceRequest] = useTableColumn(TableIdEnum.LEAD_RECALL, columns, [])
  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])

  const onTableChange: TableProps<LeadIntentionAndPipelineItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<LeadIntentionAndPipelineListFilter>(sorter)
    setFilter(sort)
  }

  return (
    <>
      <PageHeader title="待复呼线索列表" />
      <Card>
        <CommonFilters
          className="custom-Label"
          itemList={recallFilterItems}
          onSubmit={(filter) => {
            setFilter({ ...filter, page: 1 })
          }}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card>
        <ConfigTableHeader
          columnConfigs={columnConfigs}
          tableId={TableIdEnum.LEAD_RECALL}
          onSuccess={forceRequest}
        />
        <TableDownload
          className="ml20"
          exportMethod={exportLeadIntentionAndPipelineList}
          filter={filter}
        />
        <Table
          style={{ maxWidth: tableXWidth + 'px' }}
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={recallLeadList}
          columns={columnProps}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}
